<template>
  <div class="field-number-edit position-relative">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>

    <input
      type="text"
      inputmode="decimal"
      :value="inputDisplayValue"
      :id="computedFieldId"
      class="form-control field-number"
      :class="{
        'calculated-field': isLocked,
        'readonly-field': calculated || field.readonly,
        'currency-field': field.currency,
      }"
      style="background: none"
      :placeholder="placeholder"
      @input="onNumberInput($event)"
      @paste="onNumberPaste"
      @blur="onBlurChange($event.target.value)"
      @focus="onFocus"
    />
  </div>
</template>

<script>
import { parseDisplayedNumberSync } from "@/utils/numberFormatter";

export default {
  name: "EditFieldNumber",
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [Number, String, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activelyEditing: false,
      /** Mientras el usuario escribe, guardamos el texto aquí para no perder punto/coma (el padre convierte "5." → 5 y re-render borra el punto) */
      localInputValue: null,
    };
  },
  watch: {
    /** Valor externo (p. ej. botones «Apply amount» en QuickAddCollectModal): refrescar el input. */
    value(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.localInputValue = null;
    },
  },
  computed: {
    /** Valor mostrado en el input: mientras editamos usamos localInputValue; si no, el valor del modelo (displayValue) */
    inputDisplayValue() {
      if (this.localInputValue !== null) {
        return this.localInputValue;
      }
      return this.displayValue ?? '';
    },
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
    placeholder() {
      return this.field.label || this.field.key || "";
    },
    computedFieldId() {
      return this.fieldId || `field-number-${this.field.key}`;
    },
    isLocked() {
      if (this.disableLock) {
        return false;
      }
      if (this.activelyEditing) {
        return false;
      }
      if (!this.value) {
        return false;
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
  },
  methods: {
    /** True si el valor mostrado es numéricamente 0 (0, "0", "0.0", etc.), sin tratar '' ni null. */
    isNumericZero(v) {
      if (v === null || v === undefined || v === "" || Array.isArray(v)) {
        return false;
      }
      const n = Number(v);
      return !Number.isNaN(n) && n === 0;
    },
    onNumberInput(event) {
      let value = event.target.value;
      value = value.replace(/,/g, '.');
      value = value.replace(/[^0-9.-]/g, '');
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      if (value.startsWith('.')) {
        value = '0' + value;
      }
      if (value.startsWith('-.')) {
        value = '-0' + value.substring(1);
      }
      this.localInputValue = value === '' ? '' : value;
      this.onFieldChange(value === '' ? null : value);
    },
    /**
     * Pegar un número copiado de la pantalla («1,338.0885 USD»): onNumberInput toma toda coma
     * como decimal y lo dejaba en 1.3380885. Se lee con el separador de miles y después sigue
     * el camino de siempre. Escribir con el teclado no cambia.
     */
    onNumberPaste(event) {
      const pasted = event.clipboardData ? event.clipboardData.getData("text") : "";
      const number = parseDisplayedNumberSync(pasted);
      if (number === null) return;
      event.preventDefault();
      const input = event.target;
      const current = String(input.value || "");
      const start = input.selectionStart != null ? input.selectionStart : current.length;
      const end = input.selectionEnd != null ? input.selectionEnd : current.length;
      input.value = current.slice(0, start) + number + current.slice(end);
      this.onNumberInput({ target: input });
    },
    onBlurChange(newValue) {
      this.onBlur();
      // Siempre transformar decimal con coma a punto en campos number
      if (typeof newValue === 'string' && newValue.includes(',')) {
        newValue = newValue.replace(/,/g, '.');
      }
      this.onFieldChange(newValue === '' ? null : newValue);
      this.localInputValue = null;
    },
    onFieldChange(newValue) {
      this.activelyEditing = true;
      this.$emit("input", newValue);
    },
    onBlur() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },
    onFocus() {
      if (this.localInputValue === null) {
        const v = this.displayValue;
        const canEdit = !this.calculated && !this.field.readonly;
        if (canEdit && this.isNumericZero(v)) {
          this.localInputValue = "";
          this.onFieldChange(null);
        } else {
          this.localInputValue =
            v !== null && v !== undefined && v !== "" ? String(v) : "";
        }
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.field-number-edit {
  width: 100%;
}

.form-control {
  padding: 0 7px !important;
}

.readonly-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.8;
}

.calculated-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  cursor: not-allowed !important;
}

.readonly-field:focus {
  box-shadow: none !important;
  border-color: #dee2e6 !important;
}

.readonly-field::placeholder {
  color: #adb5bd !important;
}

</style>

